// src/redux/slices/drawers/controlled_substance_logging/medicationConfigDrawerSlice.ts
import { createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { csl } from "lobbie";
import { BASE_API_URL, BASE_SECURE_ROUTE } from "src/constants";
import { handleBackendHttpError } from "src/redux/handleBackendHttpError";
import { RootState } from "src/redux/store";
import { ESearchTableType } from "src/types/global_enums";
import { generateUUID, notify } from "src/utils";
import { triggerSearchTableRefetch } from "../../searchTableRefetchSlice";
import { createDrawerSlice, IBaseDrawerState } from "../createDrawerSlice";

export const NEW_MEDICATION_ID = -1;

interface INdcVariantBase {
    id?: number;
    databaseId?: number;
    medicationId?: number;
    ndcCode?: string;
    deaSchedule?: string;
    strength?: string;
    strengthUnit?: string;
    dosageForm?: string;
    billingUnit?: string;
    smallestDispensableUnit?: number;
    // Add FDA-specific fields
    brandName?: string;
    genericName?: string;
    manufacturer?: string;
    packageDescription?: string;
    marketingStartDate?: string;
}

export interface IMedicationConfigDrawerState extends IBaseDrawerState {
    medicationId?: number;
    selectedLocationId?: number;
    newMedicationName?: string;
    isCreatingNew: boolean;
    isLoading: boolean;
    isSubmitting: boolean;
    error: string | null;
    ndcVariants: INdcVariantBase[];
    originalNdcVariants: csl.INdcVariant[];
}

const hasVariantChanges = (current: INdcVariantBase, original: csl.INdcVariant) =>
    JSON.stringify(current) !== JSON.stringify(original);

export const initialMedicationConfigDrawerState: IMedicationConfigDrawerState = {
    id: "",
    show: false,
    medicationId: undefined,
    selectedLocationId: undefined,
    newMedicationName: undefined,
    isCreatingNew: false,
    isLoading: false,
    isSubmitting: false,
    error: null,
    ndcVariants: [],
    originalNdcVariants: [],
};

interface ISelectMedicationPayload {
    medication: csl.IMedication | undefined;
    locationId?: number;
    locationName?: string;
}

export const selectMedication = createAsyncThunk<
    csl.INdcVariant[],
    ISelectMedicationPayload,
    { rejectValue: string }
>("medicationConfigDrawer/selectMedication", async (payload, { dispatch, rejectWithValue }) => {
    const { medication, locationId } = payload;
    if (!medication) {
        dispatch(
            setMedicationConfig({
                isCreatingNew: false,
                medicationId: undefined,
                newMedicationName: undefined,
                ndcVariants: [],
                selectedLocationId: locationId, // Preserve the locationId even when clearing
            }),
        );
        return [];
    }
    // Set basic medication info first
    dispatch(
        setMedicationConfig({
            isCreatingNew: medication.id === NEW_MEDICATION_ID,
            medicationId: medication.id === NEW_MEDICATION_ID ? undefined : medication.id,
            selectedLocationId: medication.locationId || locationId,
            newMedicationName: medication.id === NEW_MEDICATION_ID ? medication.name : undefined,
        }),
    );
    if (medication.id !== NEW_MEDICATION_ID) {
        try {
            const response = await axios.post(
                `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/ndc-variants/search`,
                {
                    medicationId: medication.id,
                    pageNumber: 1,
                    rowsPerPage: 100,
                },
                { withCredentials: true },
            );
            const transformedData = (response.data || []).map((variant: any) => ({
                ...variant,
                databaseId: variant.id,
            }));
            // Store original variants
            dispatch(setMedicationConfig({ originalNdcVariants: transformedData }));
            return transformedData;
        } catch (error) {
            return rejectWithValue("Failed to load NDC variants");
        }
    }
    dispatch(setMedicationConfig({ originalNdcVariants: [] }));
    return [];
});
export const saveMedication = createAsyncThunk<
    void,
    { formData: { ndcVariants: INdcVariantBase[] } },
    { state: RootState; rejectValue: string }
>(
    "medicationConfigDrawer/saveMedication",
    async ({ formData }, { getState, dispatch, rejectWithValue }) => {
        try {
            const state = getState().medicationConfigDrawer;
            let medicationId = state.medicationId;
            // Create new medication if needed
            if (state.isCreatingNew && state.newMedicationName && state.selectedLocationId) {
                const medicationResponse = await axios.post(
                    `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/medication`,
                    {
                        name: state.newMedicationName,
                        locationId: state.selectedLocationId,
                    },
                    { withCredentials: true },
                );

                if (!medicationResponse?.data) {
                    throw new Error("Failed to create medication");
                }
                medicationId = medicationResponse.data.id;
            }
            // Handle NDC variants if we have a valid medicationId
            if (medicationId && formData.ndcVariants.length > 0) {
                const ndcVariants = formData.ndcVariants.map((variant) => ({
                    ...variant,
                    id: variant.databaseId || variant.id,
                    medicationId,
                }));
                const newVariants = ndcVariants.filter((variant) => !variant.id);
                const existingVariants = ndcVariants.filter((variant) => {
                    if (!variant.id) return false;
                    const originalVariant = state.originalNdcVariants.find(
                        (orig) => orig.id === variant.id,
                    );
                    return originalVariant && hasVariantChanges(variant, originalVariant);
                });
                // Save new variants
                if (newVariants.length > 0) {
                    await axios.post(
                        `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/ndc-variants`,
                        { medicationId, ndcVariants: newVariants },
                        { withCredentials: true },
                    );
                }
                // Update existing variants
                if (existingVariants.length > 0) {
                    await axios.put(
                        `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/csl/ndc-variants`,
                        { medicationId, ndcVariants: existingVariants },
                        { withCredentials: true },
                    );
                }
            }
            dispatch(
                triggerSearchTableRefetch([
                    ESearchTableType.CSL_CURRENT_INVENTORY,
                    ESearchTableType.CSL_CONTAINER_LOGS,
                ]),
            );
            notify({
                level: "success",
                title: "Success",
                message: state.isCreatingNew
                    ? "Medication created successfully"
                    : "Medication updated successfully",
            });
        } catch (error: any) {
            return rejectWithValue(JSON.stringify(handleBackendHttpError(error)));
        }
    },
);

interface IUpdateNdcVariantPayload {
    index: number;
    data: Partial<INdcVariantBase>;
}

const medicationConfigDrawerSlice = createDrawerSlice({
    name: "medicationConfigDrawer",
    initialState: initialMedicationConfigDrawerState,
    reducers: {
        createNewMedication: {
            prepare: () => ({ payload: { id: generateUUID() } }),
            reducer: (state, action: PayloadAction<{ id: string }>) => ({
                ...initialMedicationConfigDrawerState,
                id: action.payload.id,
                show: true,
                isCreatingNew: true,
            }),
        },
        showMedicationConfigDrawer: {
            prepare: (medicationId: number) => ({
                payload: { medicationId, id: medicationId.toString() },
            }),
            reducer: (state, action: PayloadAction<{ id: string; medicationId: number }>) => ({
                ...initialMedicationConfigDrawerState,
                id: action.payload.id,
                show: true,
                medicationId: action.payload.medicationId,
            }),
        },
        hideMedicationConfigDrawer: (state) => ({
            ...state,
            show: false,
        }),
        setMedicationConfig: (
            state,
            action: PayloadAction<Partial<IMedicationConfigDrawerState>>,
        ) => ({
            ...state,
            ...action.payload,
        }),
        updateNdcVariant: (state, action: PayloadAction<IUpdateNdcVariantPayload>) => {
            const { index, data } = action.payload;
            if (state.ndcVariants[index]) {
                state.ndcVariants[index] = {
                    ...state.ndcVariants[index],
                    ...data,
                };
            }
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch variants
            .addCase(selectMedication.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(selectMedication.fulfilled, (state, action) => {
                state.isLoading = false;
                state.ndcVariants = action.payload;
            })
            .addCase(selectMedication.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Failed to load variants";
                notify({
                    level: "error",
                    title: "Error",
                    message: "Failed to load NDC variants",
                });
            })
            // saveMedication
            .addCase(saveMedication.pending, (state) => {
                state.isSubmitting = true;
                state.error = null;
            })
            .addCase(saveMedication.fulfilled, (state) => {
                return initialMedicationConfigDrawerState;
            })
            .addCase(saveMedication.rejected, (state, action) => {
                state.isSubmitting = false;
                state.error = action.payload || "Failed to save medication";
            });
    },
});

export const {
    createNewMedication,
    showMedicationConfigDrawer,
    hideMedicationConfigDrawer,
    setMedicationConfig,
    updateNdcVariant,
} = medicationConfigDrawerSlice.actions;

export default medicationConfigDrawerSlice.reducer;
